import { Component, Input, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FormErrorComponent } from '../form-error/form-error.component';

/** Informações do arquivo selecionado para exibição */
interface FileInfo {
  name: string;
  size: string;
  type: string;
  icon: string;
}

/**
 * IsFileFieldComponent
 *
 * Campo de upload com drag & drop, preview e feedback visual.
 * Suporta validação de extensão e exibe informações do arquivo.
 *
 * @example
 * <is-file-field
 *   label="Documento"
 *   accept=".pdf,.jpg,.png"
 *   helpText="Arraste ou clique para enviar"
 *   formControlName="document"
 *   [control]="form.get('document')"
 * ></is-file-field>
 */
@Component({
  selector: 'is-file-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, FormErrorComponent],
  templateUrl: './file-field.component.html',
  styleUrls: ['./file-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileFieldComponent),
      multi: true,
    },
  ],
})
export class FileFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() accept = '';
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;
  @Input() maxSizeMb = 10;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  fileInfo: FileInfo | null = null;
  isDragging = false;
  isDisabled = false;

  private onChange: (val: File | null) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  /** Formatos aceitos legíveis para o usuário */
  get acceptedFormatsLabel(): string {
    if (!this.accept) return '';
    return this.accept
      .split(',')
      .map((ext) => ext.trim().replace('.', '').toUpperCase())
      .join(', ');
  }

  writeValue(_val: any): void {
    this.fileInfo = null;
  }

  registerOnChange(fn: (val: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  /** Abre o seletor de arquivo nativo */
  openFilePicker(): void {
    if (!this.isDisabled) {
      this.fileInput.nativeElement.click();
    }
  }

  /** Processa arquivo selecionado via input */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.processFile(file);
  }

  /** Drag enter */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDisabled) {
      this.isDragging = true;
    }
  }

  /** Drag leave */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /** Drop */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (this.isDisabled) return;

    const file = event.dataTransfer?.files?.[0] || null;
    this.processFile(file);
  }

  /** Remove o arquivo selecionado */
  removeFile(): void {
    this.fileInfo = null;
    this.onChange(null);
    this.fileInput.nativeElement.value = '';
  }

  // ─── Privados ──────────────────────────────────────────────────────────────

  private processFile(file: File | null): void {
    if (!file) return;

    this.fileInfo = {
      name: file.name,
      size: this.formatFileSize(file.size),
      type: file.type,
      icon: this.getFileIcon(file.name),
    };

    this.onChange(file);
    this.onTouched();
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  private getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
      pdf: 'bxs-file-pdf',
      doc: 'bxs-file-doc',
      docx: 'bxs-file-doc',
      xls: 'bxs-file',
      xlsx: 'bxs-file',
      jpg: 'bxs-file-image',
      jpeg: 'bxs-file-image',
      png: 'bxs-file-image',
      webp: 'bxs-file-image',
      gif: 'bxs-file-image',
      zip: 'bxs-file-archive',
      rar: 'bxs-file-archive',
    };
    return iconMap[ext] || 'bxs-file-blank';
  }
}
